import styled from 'styled-components';
import { Card } from 'ui/card';
import { Checkbox, CHECKBOX_STATUS } from 'ui/checkbox';
import {
  body2,
  caption,
  colors,
  smallShadow,
  mediumShadow,
  spacing,
  duration,
  easing,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useSelectionContext } from '../../../contexts/SelectionContext';
import { Asset } from '../../../types';
import { ImagePreview } from '../common/ImagePreview';
import { getImageType, getFileSize } from '../../../utils/strings';
import { AssetActionMenu } from '../common/AssetActionMenu';
import { Radio } from 'ui/radio';

const AdditionalDetails = styled.div`
  display: flex;
  min-width: 0;
  ${caption};
  color: ${colors.textSecondary};

  white-space: nowrap;
  & > span:not(:last-child) {
    :after {
      padding: 0 ${spacing.half};
      content: '•';
    }
  }
`;

export const StyledCard = styled(Card)<{ $isSelected: boolean }>`
  ${smallShadow};
  background: ${({ $isSelected }) => ($isSelected ? colors.purple10 : colors.white)};
  border-radius: ${spacing.half};
  padding: ${spacing.half};
  position: relative;
  outline: none;
  border: ${spacing.eighth} solid transparent;
  transition: all ${duration.short} ${easing.exit};
  [data-test='card-preview-container']::before {
    border-radius: ${spacing.quarter};
    opacity: 0;
    background: linear-gradient(
      0deg,
      rgba(24, 4, 28, 0.6) 0.52%,
      rgba(24, 4, 28, 0.33) 43.23%,
      rgba(24, 4, 28, 0) 98.96%
    );
    content: '';
    display: block;
    position: absolute;
    width: 96%;
    height: inherit;
    transition: all ${duration.short} ${easing.exit};
  }

  &:hover {
    ${mediumShadow};
    cursor: pointer;
    [id='card-preview-container']::before {
      background: linear-gradient(
        0deg,
        rgba(24, 4, 28, 0.6) 0.52%,
        rgba(24, 4, 28, 0.33) 43.23%,
        rgba(24, 4, 28, 0) 98.96%
      );
      border-radius: ${spacing.quarter};
      content: '';
      display: block;
      position: absolute;
      width: 96%;
      height: inherit;
      opacity: 1;
      transition: all ${duration.short} ${easing.entrance};
    }
  }
  &:focus {
    border-radius: ${spacing.half};
    border: ${spacing.eighth} solid ${colors.purple50};
  }
`;

export const PreviewContainer = styled.div`
  align-items: center;
  background: ${colors.backgroundTertiary};
  border-radius: ${spacing.quarter};
  display: flex;
  height: ${pxToRem(216)};
  justify-content: center;
  overflow: hidden;
  padding: ${pxToRem(20)};
`;

export const ImageDetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${spacing.base};
  margin-bottom: ${spacing.half};
  height: ${pxToRem(44)};
  padding: 0 ${spacing.quarter};
  width: 100%;
`;

const DetailsWrapper = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: ${spacing.base};
`;

const Name = styled.div`
  ${body2};
  margin-bottom: ${spacing.half};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  
`;

const StyledCheckbox = styled(Checkbox)`
  position: absolute;
  left: ${spacing.base};
  top: ${spacing.base};
  z-index: 1;
`;

const RadioContainer = styled.div`
  position: absolute;
  left: ${spacing.base};
  top: ${spacing.base};
  z-index: 1;
`;

export const CardItem = ({
  asset,
  isPanel,
  hasManageBaseTemplatesPermission,
}: {
  asset: Asset;
  isPanel: boolean;
  hasManageBaseTemplatesPermission: boolean;
}) => {
  const { id } = asset;
  const { bulk, toggleBulkSelection, clearAll } = useSelectionContext();
  const isSelected = bulk.findIndex((selected) => selected.id === id) > -1;

  return (
    <StyledCard data-test={`card-item-${asset.id}`} $isSelected={isSelected} tabIndex="0">
      <PreviewContainer id="card-preview-container" data-test="card-preview-container">
        {isPanel ? (
          <RadioContainer>
            <Radio
              isChecked={isSelected}
              value={isSelected}
              onClick={() => {
                clearAll();
                toggleBulkSelection({ ...asset, name: asset.assetName });
              }}
            />
          </RadioContainer>
        ) : hasManageBaseTemplatesPermission ? (
          <StyledCheckbox
            status={isSelected ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
            tabIndex={undefined}
            onClick={() => toggleBulkSelection({ ...asset, name: asset.assetName })}
          />
        ) : (
          <div />
        )}
        <ImagePreview image={asset} height="100%" width="100%" />
      </PreviewContainer>
      <ImageDetailsContainer data-test={`image-details-${asset.id}`}>
        <DetailsWrapper>
          <Name>{asset.assetName}</Name>
          <AdditionalDetails>
            <span>{getImageType(asset.mimeType)}</span>
            <span>{getFileSize(asset.size)}</span>
            <span>
              {asset.width} &times; {asset.height}
            </span>
          </AdditionalDetails>
        </DetailsWrapper>
        <AssetActionMenu
          asset={asset}
          isPanel={isPanel}
          hasManageBaseTemplatesPermission={hasManageBaseTemplatesPermission}
        />
      </ImageDetailsContainer>
    </StyledCard>
  );
};
